import React, { useState, useEffect } from "react"
import Icon from "../../../components/AppIcon"
import { computeCohortAssessmentPerformance, getCohortLearners } from "../../../utils/cohortHelpers"

// Mirrors ProgramAssessmentsTab's pattern (computeProgramAssessmentPerformance)
// but scoped down to this cohort's learners via getCohortLearners instead of
// the whole program's bundled courseIds.
const CohortAssessmentsTab = ({ orgId, cohort }) => {
  const [performance, setPerformance] = useState({ totalCompleted: 0, avgScore: 0, passRate: 0 })
  const [learnerRows, setLearnerRows] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      if (!orgId || !cohort?.cohortId) return
      setLoading(true)
      const [perf, { enrollments }] = await Promise.all([
        computeCohortAssessmentPerformance(orgId, cohort),
        getCohortLearners(orgId, cohort?.cohortId, cohort?.courseId),
      ])
      setPerformance(perf)

      // Per-learner rollup: how many module quizzes + capstone they've attempted,
      // and their best average score across all attempts, so admins can see who's
      // behind on assessments, not just the cohort-wide aggregate.
      const rows = enrollments?.map((e) => {
        const quizResults = Object.values(e?.moduleQuizResults || {})
        const attemptsCount = quizResults?.filter((r) => r?.attemptCount)?.length + (e?.capstoneResult?.attemptCount ? 1 : 0)
        const scores = quizResults?.filter((r) => r?.attemptCount)?.map((r) => r?.bestScore || 0)
        if (e?.capstoneResult?.attemptCount) scores.push(e?.capstoneResult?.bestScore || 0)
        const avgScore = scores?.length ? Math.round(scores.reduce((s, v) => s + v, 0) / scores.length) : null
        return {
          userId: e?.userId,
          name: e?.learnerName || "Unnamed learner",
          email: e?.learnerEmail || e?.userId,
          attemptsCount,
          avgScore,
        }
      }) || []
      setLearnerRows(rows)
      setLoading(false)
    }
    load()
  }, [orgId, cohort?.cohortId, cohort?.courseId])

  if (loading) {
    return <div className="p-12 text-center text-muted-foreground text-sm">Loading assessment data...</div>
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-xs text-muted-foreground">Attempts Completed</p>
          <p className="text-2xl font-bold text-foreground">{performance.totalCompleted}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-xs text-muted-foreground">Average Score</p>
          <p className="text-2xl font-bold text-foreground">{performance.avgScore}%</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-xs text-muted-foreground">Pass Rate</p>
          <p className="text-2xl font-bold text-foreground">{performance.passRate}%</p>
        </div>
      </div>

      {learnerRows?.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <Icon name="FileCheck" size={24} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No learners assigned to this cohort yet.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs text-muted-foreground uppercase">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Learner</th>
                <th className="text-left px-4 py-3 font-medium">Attempts</th>
                <th className="text-left px-4 py-3 font-medium">Avg Score</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {learnerRows?.map((row) => (
                <tr key={row.userId} className="hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <p className="font-medium text-foreground">{row.name}</p>
                    <p className="text-xs text-muted-foreground">{row.email}</p>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{row.attemptsCount}</td>
                  <td className="px-4 py-3 text-muted-foreground">{row.avgScore === null ? "-" : `${row.avgScore}%`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default CohortAssessmentsTab